import { CommonModule, DOCUMENT } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { Component, Inject, OnDestroy, OnInit, Renderer2, inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TableModule } from 'primeng/table';
import { TabViewModule } from 'primeng/tabview';
import { ToastModule } from 'primeng/toast';

import {
  Customer,
  PaymentDueItem,
  PaymentDueResponse,
  SaleReturn,
} from '../../modals/product.modals';
import { KeyboardEventService } from '../../services/keyboard-event.service';
import { IndustryService } from '../../services/industry.service';
import { ProductDbService } from '../../services/productdb.service';
import { PettyCashComponent } from '../petty-cash/petty-cash.component';
import { PageHeaderComponent } from '../shared/components/page-header/page-header.component';
import { PaymentDialogComponent } from '../shared/components/payment-dialog/payment-dialog.component';
import {
  PaymentData,
  PaymentDialogConfig,
} from '../shared/interfaces/payment-dialog.interface';

@Component({
  selector: 'app-cash-management',
  standalone: true,
  imports: [
    CommonModule,
    HttpClientModule,
    FormsModule,
    ReactiveFormsModule,
    TabViewModule,
    ButtonModule,
    CardModule,
    AutoCompleteModule,
    TableModule,
    DialogModule,
    InputTextModule,
    InputNumberModule,
    DropdownModule,
    ToastModule,
    ProgressSpinnerModule,
    PettyCashComponent,
    PaymentDialogComponent,
    PageHeaderComponent,
  ],
  providers: [MessageService, ProductDbService],
  templateUrl: './cash-management.component.html',
  styleUrls: ['./cash-management.component.scss'],
})
export class CashManagementComponent implements OnInit, OnDestroy {
  /** Petty Cash tab availability (feature-flags.ts) */
  readonly isPettyCashEnabled: boolean = inject(IndustryService).isPettyCashEnabled();
  // Default to the Credit Payment tab: index 1 after Petty Cash, or 0 when Petty Cash is hidden
  activeTabIndex: number = this.isPettyCashEnabled ? 1 : 0;

  // Credit Payment Tab Variables
  customers: Customer[] = [];
  filteredCustomers: Customer[] = [];
  selectedCustomer: Customer | null = null;
  customerSearchText: string = '';

  paymentDueData: PaymentDueResponse | null = null;
  selectedPaymentItems: PaymentDueItem[] = [];

  // Sale Returns Tab Variables
  saleReturnsData: SaleReturn[] = [];
  saleReturnsSummary = {
    totalCreditAvailable: 0,
    totalRefundsProcessed: 0,
    totalReturnsCount: 0,
  };
  saleReturnsLoading: boolean = false;
  saleReturnsError: string | null = null;

  // Payment Dialog Variables
  showPaymentDialog: boolean = false;
  paymentDialogConfig: PaymentDialogConfig = {
    showRedeemPoints: false, // Not applicable for cash management
    showSplit: true,
    isExchangeOrder: false,
    isCashManagement: true,
    title: 'Credit Payment Processing',
  };
  paymentData: PaymentData = {
    totalAmount: 0,
    cashAmount: 0,
    cardAmount: 0,
    upiAmount: 0,
    splitPaymentAmounts: { cash: 0, card: 0, upi: 0 },
    cardNumber: '',
    cardTransactionId: '',
    upiId: '',
    upiTransactionId: '',
  };
  isPaymentProcessing: boolean = false;

  constructor(
    private router: Router,
    private productService: ProductDbService,
    private messageService: MessageService,
    private renderer: Renderer2,
    private keyboardEventService: KeyboardEventService,
    @Inject(DOCUMENT) private document: Document,
  ) {}

  ngOnInit() {
    this.loadCustomers();
  }

  ngOnDestroy() {}

  loadCustomers() {
    this.productService.getAllCustomers().subscribe({
      next: (response) => {
        console.log('Customers API Response:', response);
        if (
          (response.status === 201 || response.status === '201') &&
          response.data
        ) {
          this.customers = response.data;
          console.log('Loaded customers:', this.customers);
        } else {
          console.log('Unexpected response format:', response);
        }
      },
      error: (error) => {
        console.error('Error loading customers:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load customers',
          life: 4000,
        });
      },
    });
  }

  filterCustomers(event: any) {
    const query = event.query.toLowerCase();
    this.filteredCustomers = this.customers.filter(
      (customer) =>
        customer.customer_name.toLowerCase().includes(query) ||
        customer.mobile_no.includes(query),
    );
  }

  onCustomerSelect(event: any) {
    const customer = event.value || event;
    this.selectedCustomer = customer;
    this.loadPaymentDueList(customer.id);
    this.loadSaleReturns(customer.id);
  }

  loadPaymentDueList(customerId: number) {
    console.log('Loading payment due list for customer:', customerId);
    this.productService.getPaymentDueList(customerId).subscribe({
      next: (response) => {
        console.log('Payment Due API Response:', response);
        this.paymentDueData = response;
        this.selectedPaymentItems = [];
        this.calculateTotalPayment();
        console.log('Payment due data:', this.paymentDueData);
        console.log('Selected payment items:', this.selectedPaymentItems);
      },
      error: (error) => {
        console.error('Error loading payment due list:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load payment due list',
          life: 4000,
        });
      },
    });
  }

  loadSaleReturns(customerId: number) {
    console.log('Loading sale returns for customer:', customerId);
    this.saleReturnsLoading = true;
    this.saleReturnsError = null;

    this.productService.getSaleReturns(customerId).subscribe({
      next: (response) => {
        console.log('Sale Returns API Response:', response);

        // Filter sale returns by matching user_id with selected customer's mobile_no
        const allReturns = response || [];
        const customerMobileNo = this.selectedCustomer?.mobile_no;

        this.saleReturnsData = allReturns.filter(
          (item) => item.user_id.toString() === customerMobileNo,
        );

        console.log('Filtered sale returns data:', this.saleReturnsData);
        this.calculateSaleReturnsSummary();
        this.saleReturnsLoading = false;
      },
      error: (error) => {
        console.error('Error loading sale returns:', error);
        this.saleReturnsLoading = false;
        this.saleReturnsError = 'Failed to load sale returns';
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load sale returns',
          life: 4000,
        });
      },
    });
  }

  calculateSaleReturnsSummary() {
    this.saleReturnsSummary = {
      totalCreditAvailable: this.saleReturnsData
        .filter((item) => item.status === 'Credit')
        .reduce((sum, item) => sum + item.creditamount, 0),
      totalRefundsProcessed: this.saleReturnsData
        .filter((item) => item.status === 'Refund')
        .reduce((sum, item) => sum + item.refundtotal, 0),
      totalReturnsCount: this.saleReturnsData.length,
    };
  }

  calculateTotalPayment() {
    this.paymentData.totalAmount = this.selectedPaymentItems.reduce(
      (sum, item) => {
        const amountToPay =
          item.partialPaymentAmount && item.partialPaymentAmount > 0
            ? item.partialPaymentAmount
            : item.balanceAmountDue;
        return sum + amountToPay;
      },
      0,
    );
  }

  onPaymentItemToggle(item: PaymentDueItem, isSelected: boolean) {
    if (isSelected) {
      if (
        !this.selectedPaymentItems.find(
          (selected) => selected.saleid === item.saleid,
        )
      ) {
        this.selectedPaymentItems.push(item);
      }
    } else {
      this.selectedPaymentItems = this.selectedPaymentItems.filter(
        (selected) => selected.saleid !== item.saleid,
      );
    }
    this.calculateTotalPayment();
  }

  proceedToPayment() {
    if (this.selectedPaymentItems.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'No Items Selected',
        detail: 'Please select at least one payment item to proceed',
        life: 4000,
      });
      return;
    }

    // Reset payment form first
    this.resetPaymentForm();

    // Then calculate total and populate data
    this.calculateTotalPayment();

    // Populate customer information in payment data
    if (this.selectedCustomer) {
      this.paymentData.mobileNumber = this.selectedCustomer.mobile_no;
      this.paymentData.customerName = this.selectedCustomer.customer_name;
    }

    // Open payment dialog within cash management
    this.showPaymentDialog = true;
  }

  resetPaymentForm() {
    // Preserve totalAmount and customer info, reset only payment inputs
    const currentTotal = this.paymentData?.totalAmount || 0;
    const currentMobile = this.paymentData?.mobileNumber || '';
    const currentName = this.paymentData?.customerName || '';

    this.paymentData = {
      totalAmount: currentTotal,
      mobileNumber: currentMobile,
      customerName: currentName,
      cashAmount: 0,
      cardAmount: 0,
      upiAmount: 0,
      splitPaymentAmounts: { cash: 0, card: 0, upi: 0 },
      cardNumber: '',
      cardTransactionId: '',
      upiId: '',
      upiTransactionId: '',
    };
  }

  processCustomerPayment() {
    if (!this.selectedCustomer) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No customer selected',
        life: 4000,
      });
      return;
    }

    const splitPaymentTotal =
      (this.paymentData.splitPaymentAmounts?.cash || 0) +
      (this.paymentData.splitPaymentAmounts?.card || 0) +
      (this.paymentData.splitPaymentAmounts?.upi || 0);
    const usingSplitPayment = splitPaymentTotal > 0;

    const finalCashAmount = usingSplitPayment
      ? this.paymentData.splitPaymentAmounts?.cash || 0
      : this.paymentData.cashAmount || 0;
    const finalCardAmount = usingSplitPayment
      ? this.paymentData.splitPaymentAmounts?.card || 0
      : this.paymentData.cardAmount || 0;
    const finalUpiAmount = usingSplitPayment
      ? this.paymentData.splitPaymentAmounts?.upi || 0
      : this.paymentData.upiAmount || 0;

    const totalEntered = finalCashAmount + finalCardAmount + finalUpiAmount;

    if (totalEntered <= 0) {
      this.messageService.add({
        severity: 'error',
        summary: 'Payment Required',
        detail: 'Please enter a payment amount',
        life: 4000,
      });
      return;
    }

    this.isPaymentProcessing = true;

    // Prepare payment payload for credit payment API
    const payload = {
      mobile: this.selectedCustomer.mobile_no,
      name: this.selectedCustomer.customer_name,
      discpercentage: 0,
      discount: 0,
      total: this.paymentData.totalAmount,
      transactionid: Date.now().toString(),
      description: `Credit payment for ${this.selectedCustomer.customer_name}`,
      amount: totalEntered,
      upiDetails: {
        upiId: this.paymentData.upiId || null,
        upiAmount: finalUpiAmount,
        transactionId: this.paymentData.upiTransactionId || null,
      },
      cardDetails: {
        name: this.paymentData.cardNumber || null,
        number: this.paymentData.cardNumber
          ? parseInt(this.paymentData.cardNumber)
          : 0,
        expiry: null,
        amount: finalCardAmount,
        transactionId: this.paymentData.cardTransactionId || null,
      },
      cashDetails: {
        cashAmount: finalCashAmount.toString(),
      },
      saledetails: this.selectedPaymentItems.map((item) => {
        const amountToPay =
          item.partialPaymentAmount && item.partialPaymentAmount > 0
            ? item.partialPaymentAmount
            : item.balanceAmountDue;
        return {
          sale_uuid: item.saleid,
          amount: amountToPay.toString(),
        };
      }),
    };

    this.productService
      .processCustomerPayment(this.selectedCustomer.id, payload)
      .subscribe({
        next: (response) => {
          this.isPaymentProcessing = false;
          if (
            response.code === '200' ||
            response.code === '201' ||
            response.code === 200 ||
            response.code === 201 ||
            response.status === 'success' ||
            (response.message &&
              response.message.toLowerCase().includes('success'))
          ) {
            this.messageService.add({
              severity: 'success',
              summary: 'Payment Successful',
              detail:
                response.message || 'Credit payment processed successfully',
              life: 4000,
            });
            this.showPaymentDialog = false;
            this.resetCreditPaymentForm();
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Payment Failed',
              detail: response.message || 'Failed to process credit payment',
              life: 4000,
            });
          }
        },
        error: (error) => {
          this.isPaymentProcessing = false;
          console.error('Credit payment error:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Payment Failed',
            detail: error.error?.message || 'Failed to process credit payment',
            life: 4000,
          });
        },
      });
  }

  resetCreditPaymentForm() {
    this.selectedCustomer = null;
    this.customerSearchText = '';

    if (this.paymentDueData?.data) {
      this.paymentDueData.data.forEach((item) => {
        item.partialPaymentAmount = undefined;
      });
    }

    this.paymentDueData = null;
    this.selectedPaymentItems = [];

    // Reset sale returns
    this.saleReturnsData = [];
    this.saleReturnsSummary = {
      totalCreditAvailable: 0,
      totalRefundsProcessed: 0,
      totalReturnsCount: 0,
    };
    this.saleReturnsError = null;

    // Fully reset payment data after successful payment
    this.paymentData = {
      totalAmount: 0,
      cashAmount: 0,
      cardAmount: 0,
      upiAmount: 0,
      splitPaymentAmounts: { cash: 0, card: 0, upi: 0 },
      cardNumber: '',
      cardTransactionId: '',
      upiId: '',
      upiTransactionId: '',
    };
  }

  goBackToHome() {
    this.router.navigate(['/dashboard']);
  }

  onPaymentProcess(paymentData: PaymentData) {
    // Update local payment data with the data from shared dialog
    this.paymentData = paymentData;
    this.isPaymentProcessing = true;
    this.processCustomerPayment();
  }

  onPaymentDialogClose() {
    // Reset payment amounts when dialog is closed without completing payment
    // This ensures fresh state for next payment
    this.paymentData.cashAmount = 0;
    this.paymentData.cardAmount = 0;
    this.paymentData.upiAmount = 0;
    this.paymentData.splitPaymentAmounts = { cash: 0, card: 0, upi: 0 };
    this.paymentData.cardNumber = '';
    this.paymentData.cardTransactionId = '';
    this.paymentData.upiId = '';
    this.paymentData.upiTransactionId = '';
  }

  getCustomerDisplayText(customer: Customer): string {
    return `${customer.customer_name} - ${customer.mobile_no}`;
  }

  isPaymentItemSelected(item: PaymentDueItem): boolean {
    return this.selectedPaymentItems.some(
      (selected) => selected.saleid === item.saleid,
    );
  }

  onPartialAmountChange(item: PaymentDueItem): void {
    if (
      item.partialPaymentAmount !== undefined &&
      item.partialPaymentAmount !== null
    ) {
      item.partialPaymentAmount =
        Math.round(item.partialPaymentAmount * 100) / 100;

      if (item.partialPaymentAmount < 0) {
        item.partialPaymentAmount = 0;
      }

      if (item.partialPaymentAmount > item.balanceAmountDue) {
        item.partialPaymentAmount =
          Math.round(item.balanceAmountDue * 100) / 100;
        this.messageService.add({
          severity: 'warn',
          summary: 'Invalid Amount',
          detail: 'Partial amount cannot exceed balance due',
          life: 3000,
        });
      }
    }

    if (this.isPaymentItemSelected(item)) {
      this.calculateTotalPayment();
    }
  }
}
